/**
 * Dynamic bot-protected domains config client.
 * Fetches tiered config from backend with ETag caching.
 * Falls back to baked-in defaults if endpoint is unavailable.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const LS_JSON = 'ac:bot_config:json';
const LS_ETAG = 'ac:bot_config:etag';
const LS_EXP = 'ac:bot_config:expires_at_ms';

// Baked-in fallback (used if endpoint and cache both fail)
const FALLBACK_CONFIG = {
  version: 0,
  ttl_seconds: 3600,
  tierA_exact: [
    'nytimes.com', 'linkedin.com', 'walmart.com', 'target.com',
    'bestbuy.com', 'costco.com', 'homedepot.com', 'lowes.com',
    'wayfair.com', 'zillow.com', 'indeed.com', 'glassdoor.com',
    'ticketmaster.com', 'stubhub.com', 'netflix.com', 'hulu.com',
    'disneyplus.com', 'spotify.com', 'chase.com', 'bankofamerica.com',
    'wellsfargo.com', 'capitalone.com', 'paypal.com', 'uber.com',
    'doordash.com', 'instacart.com', 'ebay.com', 'etsy.com',
    'shopify.com', 'nike.com',
  ],
  tierA_suffix: ['amazon.', 'amazon.co.'],
  tierB_exact: [
    'facebook.com', 'instagram.com', 'x.com', 'twitter.com',
    'microsoft.com', 'apple.com', 'google.com',
  ],
  tierB_suffix: ['gstatic.', 'fbcdn.'],
  notes: {
    tierA_message: 'This site often blocks automated scans. Expect partial results.',
    tierB_message: 'This site may limit automated scans. Results can vary.',
  },
};

function loadCached() {
  try {
    const raw = localStorage.getItem(LS_JSON);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function isCacheFresh() {
  const exp = Number(localStorage.getItem(LS_EXP) || '0');
  return Date.now() < exp;
}

function storeConfig(config, etag) {
  try {
    localStorage.setItem(LS_JSON, JSON.stringify(config));
    if (etag) localStorage.setItem(LS_ETAG, etag);
    localStorage.setItem(LS_EXP, String(Date.now() + (config.ttl_seconds || 86400) * 1000));
  } catch { /* localStorage full or unavailable */ }
}

/**
 * Get config immediately (cached or fallback). Zero network lag.
 */
export function getBotConfigSync() {
  if (isCacheFresh()) {
    const cached = loadCached();
    if (cached) return cached;
  }
  const stale = loadCached();
  return stale || FALLBACK_CONFIG;
}

/**
 * Refresh config from backend in background. Uses ETag for 304 optimization.
 */
export async function refreshBotConfig() {
  const cached = loadCached();
  const etag = localStorage.getItem(LS_ETAG);

  try {
    const headers = {};
    if (etag) headers['If-None-Match'] = etag;

    const res = await fetch(`${API_BASE}/api/v1/config/bot_protected_domains`, {
      headers,
      cache: 'no-store',
    });

    if (res.status === 304 && cached) {
      const ttl = cached.ttl_seconds || 86400;
      localStorage.setItem(LS_EXP, String(Date.now() + ttl * 1000));
      return cached;
    }

    if (!res.ok) return cached || FALLBACK_CONFIG;

    const json = await res.json();
    const newEtag = res.headers.get('ETag') || '';
    storeConfig(json, newEtag);
    return json;
  } catch {
    return cached || FALLBACK_CONFIG;
  }
}

/**
 * Classify a domain against the current config.
 * Returns { tier: 'A'|'B'|null, message: string }
 */
export function classifyDomain(domainRaw, config) {
  if (!domainRaw) return { tier: null, message: '' };
  const domain = domainRaw.trim().toLowerCase().replace(/^www\./, '');

  const matchesExact = (list) => list.some((d) => domain === d);
  const matchesSuffix = (suffixes) => suffixes.some((s) => domain.includes(s));

  if (matchesExact(config.tierA_exact || []) || matchesSuffix(config.tierA_suffix || [])) {
    return { tier: 'A', message: config.notes?.tierA_message || FALLBACK_CONFIG.notes.tierA_message };
  }

  if (matchesExact(config.tierB_exact || []) || matchesSuffix(config.tierB_suffix || [])) {
    return { tier: 'B', message: config.notes?.tierB_message || FALLBACK_CONFIG.notes.tierB_message };
  }

  return { tier: null, message: '' };
}

/**
 * Extract domain from user input (handles "nytimes.com", "https://www.nytimes.com/foo", etc.)
 */
export function extractDomain(input) {
  if (!input || !input.trim()) return null;
  let cleaned = input.trim().toLowerCase();
  if (!cleaned.startsWith('http://') && !cleaned.startsWith('https://')) {
    cleaned = 'https://' + cleaned;
  }
  try {
    let hostname = new URL(cleaned).hostname;
    if (hostname.startsWith('www.')) hostname = hostname.slice(4);
    return hostname;
  } catch { return null; }
}
